import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Loader2 } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { decideNextLearningAction } from "@/lib/learning-engine/decideNextLearningAction";
import { trackSeatEvent } from "@/lib/telemetry/seatTelemetry";
import { resolveStartLearningDestination, START_LEARNING_FALLBACK } from "@/lib/learners/startLearning";

type Props = Pick<ButtonProps, "className" | "size" | "variant"> & {
  decide?: typeof decideNextLearningAction;
  onBeforeNavigate?: () => void;
};

/** Literal Start Learning: asks the canonical engine for the next action, then navigates there. */
const StartLearningButton = ({ decide = decideNextLearningAction, onBeforeNavigate, className, size, variant }: Props) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const inFlight = useRef(false);

  const handleClick = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true);
    trackSeatEvent("start_learning_clicked", { surface: "onboarding" });
    let destination = START_LEARNING_FALLBACK;
    try {
      destination = resolveStartLearningDestination(await decide());
    } catch {
      destination = START_LEARNING_FALLBACK;
    }
    onBeforeNavigate?.();
    navigate(destination);
  }, [decide, navigate, onBeforeNavigate]);

  return (
    <Button
      type="button"
      onClick={handleClick}
      className={className}
      size={size}
      variant={variant}
      aria-busy={pending || undefined}
      aria-disabled={pending || undefined}
    >
      {pending ? (
        <span className="inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Starting…
        </span>
      ) : (
        <>
          Start Learning <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
        </>
      )}
    </Button>
  );
};

export default StartLearningButton;
